import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { apiFetch, API_BASE } from "../utils/apiClient";
import { toDateSafe } from "../utils/toDateSafe";
import UploadPreview, { appendPickedFiles } from "./UploadPreview";
import TaskText from "./TaskText";

/**
 * صفحة "مهامي" الموحّدة لكل الموظفات (انوار، بنين، الورشة، المصمم...).
 * - employeeName: الاسم المحفوظ بحقل employee للتاسك (وبه تنكتب تعليقاتها)
 * - title: الاسم الظاهر بالعنوان (افتراضياً نفس employeeName)
 * - backPath: مسار زر الرجوع (اختياري)
 */

const PRIORITY_STYLE = {
  High: { label: "عالية", bg: "#fdecea", color: "#c62828", dot: "#d32f2f" },
  Medium: { label: "متوسطة", bg: "#fff8e1", color: "#8d6e00", dot: "#f9a825" },
  Low: { label: "منخفضة", bg: "#e8f5e9", color: "#2e7d32", dot: "#2e7d32" },
};

const STATUS_STYLE = {
  pending: { label: "جديدة", color: "#6d4c41", bg: "#efebe9", border: "#a1887f" },
  inProgress: { label: "قيد العمل", color: "#1565c0", bg: "#e3f2fd", border: "#1e88e5" },
  overdue: { label: "متأخرة", color: "#c62828", bg: "#fdecea", border: "#e53935" },
  completed: { label: "مكتملة", color: "#2e7d32", bg: "#e8f5e9", border: "#43a047" },
};

const DATE_RANGES = [
  { value: "all", label: "كل التواريخ" },
  { value: "today", label: "اليوم" },
  { value: "week", label: "آخر 7 أيام" },
  { value: "month", label: "آخر 30 يوم" },
  { value: "custom", label: "تحديد فترة..." },
];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const fmtDate = (ts) => toDateSafe(ts)?.toLocaleDateString("ar-IQ", { year: "numeric", month: "short", day: "numeric" }) || "—";
const fmtDateTime = (ts) => toDateSafe(ts)?.toLocaleString("ar-IQ") || "—";
const timeOf = (ts) => toDateSafe(ts)?.getTime() || 0;

const isOverdue = (t) =>
  t.status !== "completed" && t.deadline && startOfDay(new Date(t.deadline)) < startOfDay(new Date());

const visualStatus = (t) => (t.status === "completed" ? "completed" : isOverdue(t) ? "overdue" : t.status === "inProgress" ? "inProgress" : "pending");

// نص عادي من HTML (للبحث داخل التاسكات المنسّقة)
const plainOf = (t) => `${t.text || ""} ${(t.textHtml || "").replace(/<[^>]+>/g, " ")}`;

function StatPill({ label, count, color, bg, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      sx={{
        flex: "1 1 120px",
        cursor: "pointer",
        p: 1.5,
        borderRadius: 2,
        bgcolor: active ? color : bg,
        color: active ? "#fff" : color,
        border: `1px solid ${active ? color : "transparent"}`,
        transition: "all .15s",
        textAlign: "center",
        "&:hover": { transform: "translateY(-1px)", boxShadow: 2 },
      }}
    >
      <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2 }}>{count}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{label}</Typography>
    </Box>
  );
}

function PriorityChip({ priority }) {
  const p = PRIORITY_STYLE[priority];
  if (!p) return null;
  return (
    <Chip
      size="small"
      label={p.label}
      icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.dot, ml: "6px !important" }} />}
      sx={{ bgcolor: p.bg, color: p.color, fontWeight: 700 }}
    />
  );
}

function StatusChip({ status }) {
  const s = STATUS_STYLE[status];
  return <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700 }} />;
}

function Attachments({ images, files, onPreview, size = 76 }) {
  if (!images?.length && !files?.length) return null;
  return (
    <Box mt={1.5}>
      {images?.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap">
          {images.map((img, i) => (
            <Box
              key={i}
              component="img"
              src={img}
              alt={`img-${i}`}
              onClick={() => onPreview(img)}
              sx={{
                width: size,
                height: size,
                objectFit: "cover",
                borderRadius: 1.5,
                border: "1px solid #e0e0e0",
                cursor: "zoom-in",
                transition: "transform .15s",
                "&:hover": { transform: "scale(1.05)" },
              }}
            />
          ))}
        </Box>
      )}
      {files?.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          {files.map((file, i) => (
            <Chip
              key={i}
              size="small"
              component="a"
              clickable
              href={file}
              target="_blank"
              rel="noopener"
              label={`📎 ملف ${i + 1}`}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function Comments({ comments, onPreview }) {
  if (!comments?.length) return null;
  return (
    <Accordion disableGutters elevation={0} sx={{ mt: 1.5, border: "1px solid #eee", borderRadius: "8px !important", "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">💬 التعليقات ({comments.length})</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {comments.map((c, i) => (
          <Box key={i} sx={{ p: 1.25, mb: 1, bgcolor: "#f7f7f9", borderRadius: 1.5 }}>
            <Box display="flex" justifyContent="space-between" gap={1} flexWrap="wrap">
              <Typography variant="body2" fontWeight={700}>{c.by}</Typography>
              <Typography variant="caption" color="text.secondary">{fmtDateTime(c.timestamp)}</Typography>
            </Box>
            {c.text && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{c.text}</Typography>}
            <Attachments images={c.attachments?.images} files={c.attachments?.files} onPreview={onPreview} size={64} />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

export default function EmployeeTasksPage({ employeeName, title, backPath }) {
  const navigate = useNavigate();
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // فلاتر
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | pending | inProgress | overdue
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // الافتراضي "الأقدم أولاً" للتاسكات المفتوحة (نفس الترتيب القديم)
  const [sortOrder, setSortOrder] = useState("oldest");
  const [completedSort, setCompletedSort] = useState("newest");
  const [showCompleted, setShowCompleted] = useState(false);

  // نوافذ
  const [activeTask, setActiveTask] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentImages, setCommentImages] = useState([]);
  const [commentFiles, setCommentFiles] = useState([]);
  const [isUploadingComment, setIsUploadingComment] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [completeImages, setCompleteImages] = useState([]);
  const [completeFiles, setCompleteFiles] = useState([]);
  const [completeNote, setCompleteNote] = useState("");
  const [isSubmittingComplete, setIsSubmittingComplete] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchMyTasks = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/tasks?employee=${encodeURIComponent(employeeName)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "فشل تحميل التاسكات");
      setMyTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ خطأ في جلب التاسكات:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeName]);

  const updateTaskStatus = async (taskId, status) => {
    try {
      setUpdatingId(taskId);
      const res = await apiFetch(`${API_BASE}/tasks/${taskId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status,
          ...(status === "completed" && { completedAt: true }),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "فشل تحديث التاسك");
      }
      await fetchMyTasks();
    } catch (err) {
      console.error("❌ خطأ أثناء تحديث التاسك:", err);
    } finally {
      setUpdatingId(null);
    }
  };

  // ---------- الفلترة ----------
  const counts = useMemo(() => {
    const c = { active: 0, pending: 0, inProgress: 0, overdue: 0, completed: 0 };
    myTasks.forEach((t) => {
      const v = visualStatus(t);
      c[v] += 1;
      if (v !== "completed") c.active += 1;
    });
    return c;
  }, [myTasks]);

  // فلاتر مشتركة (بحث + أولوية + تاريخ) تنطبق على المفتوحة والمكتملة
  const matchesCommon = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = new Date();
    let from = null;
    let to = null;
    if (dateRange === "today") from = startOfDay(now);
    else if (dateRange === "week") from = startOfDay(new Date(now.getTime() - 6 * 864e5));
    else if (dateRange === "month") from = startOfDay(new Date(now.getTime() - 29 * 864e5));
    else if (dateRange === "custom") {
      if (dateFrom) from = startOfDay(new Date(dateFrom));
      if (dateTo) to = new Date(new Date(dateTo).setHours(23, 59, 59, 999));
    }

    return (t) => {
      if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
      const created = toDateSafe(t.createdAt);
      if (from && (!created || created < from)) return false;
      if (to && (!created || created > to)) return false;
      if (q) {
        const hay = [plainOf(t), t.createdBy, ...(t.comments || []).map((c) => c.text)].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    };
  }, [search, priorityFilter, dateRange, dateFrom, dateTo]);

  const activeTasks = useMemo(() => {
    const list = myTasks.filter((t) => {
      if (t.status === "completed") return false;
      if (statusFilter !== "active" && visualStatus(t) !== statusFilter) return false;
      return matchesCommon(t);
    });
    const dir = sortOrder === "newest" ? -1 : 1;
    return list.sort((a, b) => dir * (timeOf(a.createdAt) - timeOf(b.createdAt)));
  }, [myTasks, statusFilter, sortOrder, matchesCommon]);

  const completedTasks = useMemo(() => {
    const list = myTasks.filter((t) => t.status === "completed" && matchesCommon(t));
    const dir = completedSort === "newest" ? -1 : 1;
    return list.sort((a, b) => dir * (timeOf(a.completedAt || a.completionProof?.completedAt || a.createdAt) - timeOf(b.completedAt || b.completionProof?.completedAt || b.createdAt)));
  }, [myTasks, completedSort, matchesCommon]);

  const hasFilters = search || priorityFilter !== "all" || dateRange !== "all" || statusFilter !== "active";
  const clearFilters = () => {
    setSearch("");
    setPriorityFilter("all");
    setDateRange("all");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("active");
  };

  // ---------- الكارتات ----------
  const renderActiveCard = (task) => {
    const vs = visualStatus(task);
    const s = STATUS_STYLE[vs];
    return (
      <Paper
        key={task.id}
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 1.5,
          borderRadius: 2,
          border: "1px solid #e6e8ec",
          borderRight: `5px solid ${s.border}`,
          bgcolor: "#fff",
          transition: "box-shadow .15s",
          "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.07)" },
        }}
      >
        {/* رأس الكارت */}
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
          <Box display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
            <StatusChip status={vs} />
            {vs === "overdue" && task.status === "inProgress" && <StatusChip status="inProgress" />}
            <PriorityChip priority={task.priority} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            📅 {fmtDate(task.createdAt)}
          </Typography>
        </Box>

        <TaskText task={task} />

        <Attachments images={task.images} files={task.files} onPreview={setPreviewImage} />

        <Comments comments={task.comments} onPreview={setPreviewImage} />

        {/* معلومات + أزرار */}
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mt={2} pt={1.5} sx={{ borderTop: "1px dashed #e0e0e0" }}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Typography variant="caption" color={vs === "overdue" ? "error" : "text.secondary"} fontWeight={vs === "overdue" ? 700 : 400}>
              ⏳ الموعد: {task.deadline || "غير محدد"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              👤 من: {task.createdBy || "غير معروف"}
            </Typography>
          </Box>

          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setActiveTask(task);
                setCommentOpen(true);
              }}
            >
              💬 تعليق
            </Button>
            {task.status === "inProgress" ? (
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => {
                  setActiveTask(task);
                  setOpenCompleteDialog(true);
                }}
              >
                ✅ مكتمل
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  disabled={updatingId === task.id}
                  onClick={() => updateTaskStatus(task.id, "inProgress")}
                >
                  {updatingId === task.id ? <CircularProgress size={16} color="inherit" /> : "▶️ بدء العمل"}
                </Button>
                {/* زر مكتمل يظهر دائماً حتى تبين الخطوة - يتفعّل بعد بدء العمل (نفس الـworkflow القديم) */}
                <Tooltip title="اضغط «بدء العمل» أولاً، بعدها تكدر ترفع إثبات الإكمال">
                  <span>
                    <Button variant="contained" color="success" size="small" disabled>
                      ✅ مكتمل
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderCompletedCard = (task) => (
    <Paper
      key={task.id}
      elevation={0}
      sx={{ p: 1.5, mb: 1.25, borderRadius: 2, border: "1px solid #dcefdc", borderRight: "5px solid #43a047", bgcolor: "#fbfefb" }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
        <Box display="flex" gap={0.75} alignItems="center">
          <StatusChip status="completed" />
          <PriorityChip priority={task.priority} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          ✅ أُكمل: {fmtDateTime(task.completedAt || task.completionProof?.completedAt)}
        </Typography>
      </Box>
      <TaskText task={task} sx={{ color: "#455a64", fontSize: 15 }} />
      <Attachments images={task.images} files={task.files} onPreview={setPreviewImage} size={60} />
      {task.completionProof && (task.completionProof.note || task.completionProof.images?.length > 0 || task.completionProof.files?.length > 0) && (
        <Box sx={{ mt: 1.5, p: 1.25, bgcolor: "#e8f5e9", borderRadius: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={800} color="#2e7d32">
            🏁 إثبات الإكمال
          </Typography>
          {task.completionProof.note && (
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
              📝 {task.completionProof.note}
            </Typography>
          )}
          <Attachments images={task.completionProof.images} files={task.completionProof.files} onPreview={setPreviewImage} size={64} />
        </Box>
      )}
      <Comments comments={task.comments} onPreview={setPreviewImage} />
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        📅 أُرسل: {fmtDate(task.createdAt)} — 👤 {task.createdBy || "غير معروف"}
      </Typography>
    </Paper>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 6 }} dir="rtl">
      {/* العنوان */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
        <Typography variant="h5" fontWeight={800}>
          📋 مهامي ({title || employeeName})
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="تحديث">
            <IconButton onClick={() => { setLoading(true); fetchMyTasks(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {backPath && (
            <Button variant="outlined" onClick={() => navigate(backPath)}>
              ↩️ رجوع
            </Button>
          )}
        </Box>
      </Box>

      {/* ملخص الحالات (قابل للضغط كفلتر) */}
      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <StatPill label="كل المفتوحة" count={counts.active} color="#37474f" bg="#eceff1" active={statusFilter === "active"} onClick={() => setStatusFilter("active")} />
        <StatPill label="جديدة" count={counts.pending} color={STATUS_STYLE.pending.color} bg={STATUS_STYLE.pending.bg} active={statusFilter === "pending"} onClick={() => setStatusFilter("pending")} />
        <StatPill label="قيد العمل" count={counts.inProgress} color={STATUS_STYLE.inProgress.color} bg={STATUS_STYLE.inProgress.bg} active={statusFilter === "inProgress"} onClick={() => setStatusFilter("inProgress")} />
        <StatPill label="متأخرة" count={counts.overdue} color={STATUS_STYLE.overdue.color} bg={STATUS_STYLE.overdue.bg} active={statusFilter === "overdue"} onClick={() => setStatusFilter("overdue")} />
      </Box>

      {/* شريط البحث والفلاتر */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2.5, borderRadius: 2, border: "1px solid #e6e8ec", bgcolor: "#fafbfc" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="ابحث بنص التاسك أو التعليقات..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1.5, bgcolor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Box display="grid" gap={1.5} sx={{ gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)" } }}>
          <TextField select size="small" label="📅 التاريخ" value={dateRange} onChange={(e) => setDateRange(e.target.value)} sx={{ bgcolor: "#fff" }}>
            {DATE_RANGES.map((d) => (
              <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="🚩 الأولوية" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} sx={{ bgcolor: "#fff" }}>
            <MenuItem value="all">الكل</MenuItem>
            <MenuItem value="High">🔴 عالية</MenuItem>
            <MenuItem value="Medium">🟡 متوسطة</MenuItem>
            <MenuItem value="Low">🟢 منخفضة</MenuItem>
          </TextField>
          <TextField select size="small" label="↕️ الترتيب" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} sx={{ bgcolor: "#fff", gridColumn: { xs: "1 / -1", sm: "auto" } }}>
            <MenuItem value="newest">الأحدث أولاً</MenuItem>
            <MenuItem value="oldest">الأقدم أولاً</MenuItem>
          </TextField>
        </Box>

        <Collapse in={dateRange === "custom"}>
          <Box display="grid" gap={1.5} mt={1.5} sx={{ gridTemplateColumns: "1fr 1fr" }}>
            <TextField type="date" size="small" label="من" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ bgcolor: "#fff" }} />
            <TextField type="date" size="small" label="إلى" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ bgcolor: "#fff" }} />
          </Box>
        </Collapse>

        {hasFilters && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
            <Typography variant="caption" color="text.secondary">
              النتائج: {activeTasks.length} مفتوحة · {completedTasks.length} مكتملة
            </Typography>
            <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters}>
              مسح الفلاتر
            </Button>
          </Box>
        )}
      </Paper>

      {/* التاسكات المفتوحة */}
      <Typography variant="subtitle1" fontWeight={800} mb={1.5}>
        ⏳ التاسكات المفتوحة ({activeTasks.length})
      </Typography>

      {loading ? (
        <Box textAlign="center" py={5}>
          <CircularProgress />
        </Box>
      ) : activeTasks.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: 2, border: "1px dashed #cfd8dc", color: "text.secondary" }}>
          {counts.active === 0 ? "🎉 لا توجد تاسكات مفتوحة" : "لا توجد نتائج تطابق البحث/الفلتر"}
        </Paper>
      ) : (
        activeTasks.map(renderActiveCard)
      )}

      {/* التاسكات المكتملة - داخل كارد ينفتح بالضغط */}
      <Paper elevation={0} sx={{ mt: 4, borderRadius: 2, border: "1px solid #c8e6c9", overflow: "hidden" }}>
        <Box
          role="button"
          onClick={() => setShowCompleted((v) => !v)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            cursor: "pointer",
            bgcolor: "#e8f5e9",
            "&:hover": { bgcolor: "#dcedc8" },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight={800} color="#2e7d32">
              ✅ التاسكات المكتملة
            </Typography>
            <Chip size="small" label={completedTasks.length} sx={{ bgcolor: "#2e7d32", color: "#fff", fontWeight: 700 }} />
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} color="#2e7d32">
            <Typography variant="caption" fontWeight={700}>{showCompleted ? "إخفاء" : "عرض"}</Typography>
            <ExpandMoreIcon sx={{ transform: showCompleted ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
          </Box>
        </Box>
        <Collapse in={showCompleted} unmountOnExit>
          <Box p={1.5}>
            {completedTasks.length > 0 && (
              <Box display="flex" justifyContent="flex-end" mb={1}>
                <TextField select size="small" label="الترتيب" value={completedSort} onChange={(e) => setCompletedSort(e.target.value)} sx={{ minWidth: 160, bgcolor: "#fff" }}>
                  <MenuItem value="newest">الأحدث إكمالاً أولاً</MenuItem>
                  <MenuItem value="oldest">الأقدم إكمالاً أولاً</MenuItem>
                </TextField>
              </Box>
            )}
            {completedTasks.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={2}>
                لا توجد تاسكات مكتملة {hasFilters ? "تطابق الفلتر" : "بعد"}
              </Typography>
            ) : (
              completedTasks.map(renderCompletedCard)
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* نافذة التعليق */}
      <Dialog open={commentOpen} onClose={() => setCommentOpen(false)} fullWidth>
        <DialogTitle>📝 إضافة تعليق</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} label="التعليق" value={commentText} onChange={(e) => setCommentText(e.target.value)} sx={{ mb: 2, mt: 1 }} />

          <Button component="label" sx={{ mr: 2 }}>
            📷 رفع صور
            <input type="file" hidden multiple accept="image/*" onChange={(e) => appendPickedFiles(e, setCommentImages)} />
          </Button>
          <Typography display="inline">{commentImages.length} صورة</Typography>
          <UploadPreview files={commentImages} onChange={setCommentImages} />

          <Box mt={1}>
            <Button component="label" sx={{ mr: 2 }}>
              📎 رفع ملفات
              <input type="file" hidden multiple onChange={(e) => appendPickedFiles(e, setCommentFiles)} />
            </Button>
            <Typography display="inline">{commentFiles.length} ملف</Typography>
            <UploadPreview files={commentFiles} onChange={setCommentFiles} />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCommentOpen(false)}>إلغاء</Button>
          <Button
            variant="contained"
            disabled={isUploadingComment}
            startIcon={isUploadingComment ? <CircularProgress size={18} color="inherit" /> : null}
            onClick={async () => {
              if (!commentText.trim() && commentImages.length === 0 && commentFiles.length === 0) {
                alert("يرجى كتابة تعليق أو رفع ملف/صورة على الأقل");
                return;
              }
              try {
                setIsUploadingComment(true);
                const imageUrls = [];
                const fileUrls = [];
                for (const img of commentImages) {
                  const imgRef = ref(storage, `tasks/comments/${Date.now()}-${img.name}`);
                  await uploadBytes(imgRef, img);
                  imageUrls.push(await getDownloadURL(imgRef));
                }
                for (const f of commentFiles) {
                  const fRef = ref(storage, `tasks/comments/${Date.now()}-${f.name}`);
                  await uploadBytes(fRef, f);
                  fileUrls.push(await getDownloadURL(fRef));
                }

                const newComment = {
                  by: employeeName,
                  text: commentText.trim(),
                  attachments: { images: imageUrls, files: fileUrls },
                  timestamp: null, // السيرفر يحسبها فعلياً
                };

                const commentRes = await apiFetch(`${API_BASE}/tasks/${activeTask.id}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ comments: [...(activeTask.comments || []), newComment] }),
                });
                if (!commentRes.ok) {
                  const errData = await commentRes.json().catch(() => ({}));
                  throw new Error(errData.error || "فشل حفظ التعليق");
                }

                setCommentText("");
                setCommentImages([]);
                setCommentFiles([]);
                setCommentOpen(false);
                fetchMyTasks();
              } catch (err) {
                console.error("❌ خطأ أثناء حفظ التعليق:", err);
              } finally {
                setIsUploadingComment(false);
              }
            }}
          >
            {isUploadingComment ? "جاري الرفع..." : "حفظ"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* عرض الصورة بالحجم الكامل */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        fullScreen
        PaperProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.85)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" } }}
      >
        <Button
          onClick={() => setPreviewImage(null)}
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            color: "white",
            fontSize: "1rem",
            fontWeight: "bold",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
            borderRadius: "50%",
            minWidth: "40px",
            height: "40px",
          }}
        >
          ❌
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
          <img src={previewImage} alt="preview" style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: "10px", objectFit: "contain", boxShadow: "0 0 40px rgba(0,0,0,0.5)" }} />
        </Box>
      </Dialog>

      {/* إرفاق إثبات الإكمال */}
      <Dialog open={openCompleteDialog} onClose={() => setOpenCompleteDialog(false)} fullWidth>
        <DialogTitle>إرفاق إثبات الإكمال</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={2} label="ملاحظة (اختياري)" value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} sx={{ mb: 2, mt: 1 }} />

          <Button component="label" sx={{ mr: 2 }}>
            📷 رفع صور
            <input type="file" hidden multiple accept="image/*" onChange={(e) => appendPickedFiles(e, setCompleteImages)} />
          </Button>
          <Typography display="inline">{completeImages.length} صورة</Typography>
          <UploadPreview files={completeImages} onChange={setCompleteImages} />

          <Box mt={1}>
            <Button component="label" sx={{ mr: 2 }}>
              📎 رفع ملفات
              <input type="file" hidden multiple onChange={(e) => appendPickedFiles(e, setCompleteFiles)} />
            </Button>
            <Typography display="inline">{completeFiles.length} ملف</Typography>
            <UploadPreview files={completeFiles} onChange={setCompleteFiles} />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenCompleteDialog(false)}>إلغاء</Button>
          <Button
            variant="contained"
            disabled={isSubmittingComplete}
            onClick={async () => {
              try {
                setIsSubmittingComplete(true);
                const imgUrls = [];
                const fileUrls = [];
                for (const img of completeImages) {
                  const refImg = ref(storage, `tasks/completion/${Date.now()}-${img.name}`);
                  await uploadBytes(refImg, img);
                  imgUrls.push(await getDownloadURL(refImg));
                }
                for (const f of completeFiles) {
                  const refFile = ref(storage, `tasks/completion/${Date.now()}-${f.name}`);
                  await uploadBytes(refFile, f);
                  fileUrls.push(await getDownloadURL(refFile));
                }

                const completeRes = await apiFetch(`${API_BASE}/tasks/${activeTask.id}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    status: "completed",
                    completionProof: { images: imgUrls, files: fileUrls, note: completeNote.trim() },
                  }),
                });
                if (!completeRes.ok) {
                  const errData = await completeRes.json().catch(() => ({}));
                  throw new Error(errData.error || "فشل حفظ إثبات الإكمال");
                }

                setCompleteNote("");
                setCompleteFiles([]);
                setCompleteImages([]);
                setOpenCompleteDialog(false);
                fetchMyTasks();
              } catch (err) {
                console.error("❌ خطأ أثناء رفع إثبات الإكمال:", err);
              } finally {
                setIsSubmittingComplete(false);
              }
            }}
          >
            {isSubmittingComplete ? "جاري الحفظ..." : "إرسال"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
